
import React, { useState, useEffect, useRef } from 'react';

import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react"

import PlayerStatsAccordion from '../components/PlayerStatsAccordion';
import ShareResultButton from '../components/ShareResultButton';
import { fetchGameOfTheDay, checkAnswer, fetchHint } from '../services/service';

const HINT_LABELS = {
  definition: 'Definição',
  fodder: 'Sopa de Letras',
  indicator: 'Indicador',
};

function Game() {
  const [hintLoading, setHintLoading] = useState(false);
  const [answerLength, setAnswerLength] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [message, setMessage] = useState('');
  const [par, setPar] = useState(3);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hintsList, setHintsList] = useState([]);
  const [showHintMenu, setShowHintMenu] = useState(false);
  const [coloredClue, setColoredClue] = useState([]);
  const [todayDate, setTodayDate] = useState(null);
  const inputRefs = useRef([]);

  const getTodayKey = React.useCallback(() => {
    const today = new Date();
    setTodayDate(today.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }));
    return today.toISOString().slice(0, 10);
  }, []);

  const getGame = React.useCallback(() => {
    const todayKey = getTodayKey();
    const savedGame = localStorage.getItem('game');
    if (savedGame) {
      const parsed = JSON.parse(savedGame);
      if (parsed.date === todayKey) {
        return parsed.data;
      }
    }
    return fetchGameOfTheDay().then(data => {
      localStorage.setItem('game', JSON.stringify({
        date: todayKey,
        data: {
          answerLength: data.answerLength,
          par: data.par,
          clue: data.clue,
          username: data.username,
          coloredClue: data.coloredClue || data.clue.split(' ').map(word => ({ word, className: '' }))
        }
      }));
      return data;
    });
  }, [getTodayKey]);

  const saveState = React.useCallback((extra = {}) => {
    const todayKey = getTodayKey();
    const state = {
      date: todayKey,
      data: {
        coloredClue,
        inputs,
        attempts,
        hintsUsed,
        hintsList,
        isCorrect,
        message,
        ...extra
      }
    };
    localStorage.setItem('gameState', JSON.stringify(state));
  }, [attempts, coloredClue, getTodayKey, hintsUsed, hintsList, inputs, isCorrect, message]);

  const focusFirstEmptyInput = React.useCallback(() => {
    const firstEmpty = inputs.findIndex(
      (val, idx) => !val && !hintsList.some(h => h.type === 'letter' && h.letterIdx === idx)
    );
    if (firstEmpty !== -1 && inputRefs.current[firstEmpty]) {
      inputRefs.current[firstEmpty].focus();
    } else if (firstEmpty === -1 && inputRefs.current[answerLength-1]) {
      inputRefs.current[answerLength-1].focus();
    }
  }, [answerLength, inputs, hintsList]);

  const resetInputs = React.useCallback(() => {
    const newInputs = Array(answerLength).fill('');

    hintsList.forEach(hint => {
      if (hint.type === 'letter' && typeof hint.letterIdx === 'number') {
        newInputs[hint.letterIdx] = hint.letter.toUpperCase();
      }
    });

    setInputs(newInputs);
    saveState({ inputs: newInputs });

    focusFirstEmptyInput();
  }, [answerLength, focusFirstEmptyInput, hintsList, saveState]);


  useEffect(() => {
    const todayKey = getTodayKey();

    Promise.resolve(getGame()).then((data) => {
      if (!data) {
        setLoading(false);
        setMessage('Erro ao carregar o jogo do dia.');
        return;
      }

      const savedState = localStorage.getItem('gameState');
      setAnswerLength(data.answerLength);
      setPar(data.par);
      setUsername(data.username || '');
      setLoading(false);

      const initialColoredClue = data.clue ? 
        data.clue.split(' ').map(word => ({ word, className: '' })) : [];
      setColoredClue(initialColoredClue);

      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.date === todayKey) {
          const state = parsed.data;
          setInputs(state.inputs || Array(data.answerLength).fill(''));
          setAttempts(state.attempts || 0);
          setHintsUsed(state.hintsUsed || 0);
          setHintsList(state.hintsList || []);
          setIsCorrect(state.isCorrect || false);
          setMessage(state.message || '');
          return;
        }
      }
      setInputs(Array(data.answerLength).fill(''));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setColoredClue(prevColoredClue => {
      if (prevColoredClue.length === 0) return prevColoredClue;
      
      return prevColoredClue.map((item, idx) => {
        const hint = hintsList.find(h => h.indexes && h.indexes.includes(idx));
        if (hint) {
          return { ...item, className: `clue-${hint.type}` };
        }
        return { ...item, className: '' };
      });
    });
  }, [hintsList]);
 

  useEffect(() => {
    if (!loading) {
      focusFirstEmptyInput();
    }
  }, [inputs, hintsList, loading, focusFirstEmptyInput]);    

  useEffect(() => {
    const adjustInputSizes = () => {
      const inputsRow = document.querySelector('.inputs-row');
      const inputBoxes = inputsRow?.querySelectorAll('.input-box');
      if (!inputBoxes || inputBoxes.length === 0) return;

      const container = document.querySelector('.App-container');
      if (!container) return;

      const currentAnswerLength = inputBoxes.length;
      const containerWidth = container.offsetWidth;
      const containerPadding = 60;
      const availableWidth = containerWidth - containerPadding;
      
      if (availableWidth < 100) return;
      
      // Calcula espaço para gaps
      const gapCount = currentAnswerLength - 1;
      let baseGapSize;
      if (currentAnswerLength > 15) baseGapSize = 0;
      else if (currentAnswerLength > 12) baseGapSize = 1;
      else if (currentAnswerLength > 8) baseGapSize = 2;
      else baseGapSize = 3;
      
      const totalGapSpace = gapCount * baseGapSize;
      const idealSize = Math.floor((availableWidth - totalGapSpace) / currentAnswerLength);
      
      // Define tamanhos
      let minSize, maxSize;
      if (currentAnswerLength > 15) {
        minSize = 6;
        maxSize = 15;
      } else if (currentAnswerLength > 12) {
        minSize = 8;
        maxSize = 20;
      } else if (currentAnswerLength > 8) {
        minSize = 10;
        maxSize = 25;
      } else {
        minSize = 12;
        maxSize = 30;
      }
      
      const finalSize = Math.max(minSize, Math.min(maxSize, idealSize));
      if (finalSize < 5 || finalSize > 50) return;
      
      const fontSize = Math.max(0.4, finalSize * 0.045);
      
      // Aplica os estilos
      inputBoxes.forEach(input => {
        input.style.width = `${finalSize}px`;
        input.style.height = `${finalSize}px`;
        input.style.fontSize = `${fontSize}rem`;
      });
      
      inputsRow.style.gap = `${baseGapSize}px`;
      inputsRow.style.padding = `0 2px`;
    };

    // Executa com delay para garantir que os inputs foram renderizados
    const timer = setTimeout(() => {
      // Executa até conseguir aplicar os estilos
      const checkAndAdjust = () => {
        const inputsRow = document.querySelector('.inputs-row');
        const inputBoxes = inputsRow?.querySelectorAll('.input-box');
        if (inputBoxes && inputBoxes.length > 0) {
          adjustInputSizes();
        } else {
          // Tenta novamente em 100ms se ainda não renderizou
          setTimeout(checkAndAdjust, 100);
        }
      };
      checkAndAdjust();
    }, 300);

    // Listener para resize
    const handleResize = () => adjustInputSizes();
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleInputChange = (idx, value) => {
    let chars = value.toUpperCase().replace(/[^A-ZÀ-ÿ]/g, '');
    if (!chars) chars = '';
    const newInputs = [...inputs];
    let i = idx;
    for (let c of chars) {
      if (i < newInputs.length) {
        newInputs[i] = c;
        i++;
      }
    }
  setInputs(newInputs);
  saveState({ inputs: newInputs });
  if (chars.length === 1 && idx < answerLength - 1) {
      setTimeout(() => {
        if (inputRefs.current[idx + 1]) inputRefs.current[idx + 1].focus();
      }, 0);
    }
  };

  const handleRevealHint = async (hintType) => {
    if (hintType === 'letter') {
      handleRevealNextLetter();
      return;
    }
    if (hintsList.some(h => h.type === hintType) || hintLoading) return;
    setHintLoading(true);
    let res = await fetchHint(hintType);
    setHintLoading(false);
    if (!res.hint) {
      if (hintsList.some(h => h.type === hintType)) {
        setMessage('Nenhuma dica disponível deste tipo.');
      }
      else {
        setMessage('Erro ao buscar dica.');
      }
      return;
    }

    const newHint = {
      type: hintType,
      label: HINT_LABELS[hintType] || hintType,
      indexes: res.hint ? res.hint.value.split(',').map((idx) => Number(idx)) : undefined
    };
    const newHints = [
      ...hintsList,
      newHint
    ];
    const newHintsUsed = hintsUsed + 1;

    setHintsList(newHints);
    setHintsUsed(newHintsUsed);    
    setColoredClue(coloredClue.map((item, idx) => {
      if (newHint.indexes && newHint.indexes.includes(idx)) {
        return { ...item, className: `clue-${hintType}` };
      }
      return item;
    }));
    saveState({ hintsList: newHints, hintsUsed: newHintsUsed });
    setShowHintMenu(false);
    focusFirstEmptyInput();
  };

  const handleRevealNextLetter = async () => {
    const hintType = 'letter';
    const revealedLetterHints = hintsList.filter(h => h.type === hintType);
    let nextIdx = revealedLetterHints.length;
    if (nextIdx < answerLength) {
      setHintLoading(true);
      let res = await fetchHint(hintType, nextIdx);
      setHintLoading(false);
      if (res.hint && res.hint.letter) {
        const newHints = [
          ...hintsList,
          {
            type: hintType,
            label: HINT_LABELS[hintType] || hintType,
            letter: res.hint.letter.toUpperCase(),
            letterIdx: nextIdx,
          }
        ];
        const newHintsUsed = hintsUsed + 1;
        setHintsList(newHints);
        setHintsUsed(newHintsUsed);
        const newInputs = [...inputs];
        newInputs[nextIdx] = res.hint.letter.toUpperCase();
        setInputs(newInputs);
        saveState({ hintsList: newHints, hintsUsed: newHintsUsed, inputs: newInputs });
        setShowHintMenu(false);
        focusFirstEmptyInput();
      }
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (inputs[idx] === '' && idx > 0) {
        const prevIdx = idx - 1;
        setInputs(inputs.map((val, i) => (i === prevIdx ? '' : val)));
        if (inputRefs.current[prevIdx]) {
          inputRefs.current[prevIdx].focus();
        }
      } else {
        setInputs(inputs.map((val, i) => (i === idx ? '' : val)));
      }
    } else if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'ArrowLeft') {
      if (idx > 0) {
        const prevIdx = idx - 1;
        if (inputRefs.current[prevIdx]) {
          inputRefs.current[prevIdx].focus();
        }
      }
    } else if (e.key === 'ArrowRight') {
      if (idx < inputs.length - 1) {
        const nextIdx = idx + 1;
        if (inputRefs.current[nextIdx]) {
          inputRefs.current[nextIdx].focus();
        }
      }
    } else if (e.key === 'Delete') {
      setInputs(inputs.map((val, i) => (i === idx ? '' : val)));
      if (inputRefs.current[idx]) {
        inputRefs.current[idx].blur();
      }
    } else {
      if (e.key.length === 1 && /^[A-Za-zÀ-ÿ]$/.test(e.key)) {
        handleInputChange(idx, e.key);
        e.preventDefault(); // Prevent default input behavior
      }
      return; // Ignore other keys
    }
    e.preventDefault();
  };

  const handleSubmit = async () => {
    const guess = inputs.join('');
    if (guess.length < answerLength) {
      setMessage('Preencha todas as letras!');
      saveState({ message: 'Preencha todas as letras!' });
      return;
    }
    setLoading(true);
    const res = await checkAnswer(guess);
    setLoading(false);
    if (res.correct) {
      const total = attempts + hintsUsed;
      let diff = total - par;
      let msg = 'Você acertou a resposta';
      if (diff === 0) msg += ' exatamente no par!';
      else if (diff > 0) msg += ` com ${diff} a mais que o par.`;
      else msg += ` com ${Math.abs(diff)} a menos que o par.`;
      setMessage(msg);
      setIsCorrect(true);
      saveState({ isCorrect: true, message: msg });

      const todayKey = getTodayKey();
      const statsKey = 'playerStats';
      let stats = localStorage.getItem(statsKey);
      let statsObj = {
        streak: 0,
        lastWinDate: null,
        statsByDiff: {},
        totalWins: 0
      };
      if (stats) {
        try { statsObj = JSON.parse(stats); } catch {}
      }

      const today = new Date(todayKey);
      let yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().slice(0, 10);
      if (statsObj.lastWinDate === yesterdayStr) {
        statsObj.streak += 1;
      } else if (statsObj.lastWinDate !== todayKey) {
        statsObj.streak = 1;
      } 
      statsObj.lastWinDate = todayKey;

      let diffKey = (diff > 0 ? "+" : "") + diff;
      if (!statsObj.statsByDiff[diffKey]) statsObj.statsByDiff[diffKey] = 0;
      statsObj.statsByDiff[diffKey] += 1;
      statsObj.totalWins += 1;
      localStorage.setItem(statsKey, JSON.stringify(statsObj));

    } else {
      setAttempts(a => {
        const newAttempts = a + 1;
        saveState({ attempts: newAttempts, inputs: Array(answerLength).fill(''), message: 'Tente novamente!' });
        return newAttempts;
      });
      resetInputs();
      setMessage('Tente novamente!');
    }
  };


  const renderDots = (total, parVal) => {
    const totalDots = Math.max(parVal + 2, total, 6);
    return (
      <div className="dots-row">
        {Array.from({ length: totalDots }).map((_, idx) => {
          let dotClass = 'dot';
          if (idx < total) dotClass += ' dot-filled';
          if (idx === parVal) {
            if (idx < total) {
              dotClass += ' dot-par-over';
            } else {
              dotClass += ' dot-par';
            }
          }
          return (
            <div
              key={idx}
              className={dotClass}
              title={idx === parVal ? 'Par (alvo)' : ''}
            />
          );
        })}
      </div>
    );
  };

  function renderGameScreen() {
    const total = attempts + hintsUsed;

    return (
      <div className="App-container">
        <div className="today-date">{todayDate}</div>

        <p className="clue-text">
          {coloredClue.map((item, idx) => (
            <React.Fragment key={idx}>
              <span className={item.className}>{item.word}</span>{' '}
            </React.Fragment>
          ))}
          <span className="answer-length">({answerLength})</span>
        </p>

        
        {username && username.trim() !== "" && (
          <div className="clue-author">
            Enviada por <b>{username}</b>
          </div>
        )}

        <div className={`inputs-row inputs-row-length-${answerLength}`} data-length={answerLength}>
          {inputs.map((val, idx) => (
            <input
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              type="text"
              maxLength={1}
              value={val}
              onChange={(e) => handleInputChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              disabled={
                isCorrect ||
                (hintsList && hintsList.some((h) => h.type === 'letter' && h.letterIdx === idx))
              }
              className={`input-box ${isCorrect ? 'input-correct' : ''}`}
            />
          ))}
        </div>

        {renderDots(total, par)}

        {isCorrect && (
          <>
            <div className="game-total-attempts">
              Tentativas + dicas usadas: <b>{total}</b>
            </div>
            <div className="game-par">Par: <b>{par}</b></div>
          </>
        )}

        {!isCorrect && (
          <div className="button-row">
            <button onClick={handleSubmit} className="btn-submit" disabled={isCorrect}>
              Enviar
            </button>
            <button
              onClick={() => setShowHintMenu(true)}
              disabled={isCorrect || (hintsList && hintsList.length === answerLength + 3)}
              className="btn-hint"
            >
              Dica
            </button>
          </div>
        )}

        {isCorrect && (
          <ShareResultButton todayDate={todayDate} attemptsUsed={attempts} hintsUsed={hintsUsed} par={par} />
        )}

        {message && <div className="game-message">{message}</div>}

        {!isCorrect && hintsList && hintsList.length > 0 && (
          <div className="hints-revealed">
            {hintsList.map((h, i) =>
              h.type === 'letter' && h.text ? (
                <div key={i} className="hint-revealed-item">
                  <b>{h.label}:</b> {h.text}
                </div>
              ) : null
            )}
          </div>
        )}

        {showHintMenu && (
          <div className="hint-menu-overlay" onClick={() => setShowHintMenu(false)}>
            <div className="hint-menu" onClick={(e) => e.stopPropagation()}>
              <h4>Escolha a dica</h4>
              <div className="hint-menu-options">
                {hintLoading ? (
                  <div className="spinner"></div>
                ) : (
                  <>
                    {['definition', 'fodder', 'indicator'].map((type) =>
                      hintsList && hintsList.some((h) => h.type === type) ? null : (
                        <button
                          key={type}
                          onClick={() => handleRevealHint(type)}
                          className="btn-hint-option"
                          disabled={hintLoading}
                        >
                          {HINT_LABELS[type]}
                        </button>
                      )
                    )}
                    <button
                      onClick={handleRevealNextLetter}
                      disabled={
                        (hintsList &&
                          hintsList.filter((h) => h.type === 'letter').length >= answerLength) ||
                        hintLoading
                      }
                      className="btn-hint-option"
                    >
                      Letra
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowHintMenu(false)}
                className="btn-hint-close"
                disabled={hintLoading}
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }


  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Carregando...</div>
    </div>
  );

  return (
    <>
      {renderGameScreen()}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
        <div className="player-stats-accordion-box">
          <PlayerStatsAccordion />
        </div>
      </div>
      <Analytics />
      <SpeedInsights />
    </>      
  );
}

export default Game;
